import { makeAutoObservable } from 'mobx';
import { createHashHistory as createHistory } from 'history';
import uniqueId from '../../../src/utils/uniqueId';
import { app } from './app';
import { ROUTES, NEW_ROUTES } from '../constants';
import { getPathname } from '../utils';

const routes = Object.values({ ...ROUTES, ...NEW_ROUTES });
const portalPrefixOnlyRoute = /^\/[^/?#]+-\d+(?:\/\d+)?\/?$/;

let keepOnPage = false;

// To open new page Location.push()
// - '/page'
// - '/page?param=1'
// To stay on the same page with different params Location.push()
// - '?id=2'
// - '?mood=happy'

class Location {
  history = null;

  curPageKey = '';

  pathname = '';

  search = '';

  pages = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  initRedirect = () => {
    const validRoute = routes.some(d => this.pathname.indexOf(d) > -1);

    if (!validRoute) {
      const hasSlash = this.pathname[this.pathname.length - 1] === '/';
      const pathname = hasSlash ? this.pathname.substring(0, this.pathname.length - 1) : this.pathname;
      const oldDesignHomeRoute = app?.isAnonymous() ? ROUTES.WORK_REQUEST_ADD : ROUTES.MY_REQUESTS;
      const route = pathname + (app?.isNewDesign() ? NEW_ROUTES.WORK_REQUEST_ADD : oldDesignHomeRoute);
      this.history.replace(route);
    }
  }

  handleLocationChange(loc) {
    app.addEventLog({ message: 'location.handleLocationChange()', args: loc });

    let curPage = null;
    if (!keepOnPage) {
      curPage = this.openPage(loc.pathname, loc.search);
    } else {
      curPage = this.replacePage(this.pathname, this.search, loc.pathname, loc.search);
    }

    this.curPageKey = curPage.pageKey;
    this.pathname = curPage.pathname;
    this.search = curPage.search;
    keepOnPage = false;

    this.initRedirect()
  }

  setHistory(history) {
    this.history = history;
  }

  setPortalId(location) {
    const { portalID, profile } = app;
    const isOnlyQueryParams = (location + '')[0] === '?';

    const validRoute = routes.some(d => location.indexOf(d) > -1) || isOnlyQueryParams;
    location = isOnlyQueryParams ? this.pathname + location : location;

    if (!profile || !portalID) {
      const fallbackLocation = validRoute || portalPrefixOnlyRoute.test(location) ? location : NEW_ROUTES.WORK_REQUEST_ADD;
      return fallbackLocation;
    }

    location = validRoute ? location : NEW_ROUTES.WORK_REQUEST_ADD;

    keepOnPage = isOnlyQueryParams;
    location = getPathname({ portalID, profile, path: location });
    return location;
  }

  push(location) {
    const newLocation = this.setPortalId(location);

    this.history.push(newLocation);
  }

  replace(location) {
    const newLocation = this.setPortalId(location);

    this.history.replace(newLocation);
  }

  goBack() {
    this.history.goBack();
  }

  goForward() {
    this.history.goForward();
  }

  // ================= EXTRA ===================
  openPage(pathname, search) {
    const url = pathname + search;
    let curPage = this.pages.find(p => p.url === url);
    if (!curPage) {
      curPage = { url, pageKey: uniqueId(), pathname, search };
      this.pages.push(curPage);
    }
    return curPage;
  }

  switchTab(pageKeyParam) {
    let curPage = this.pages.find(p => p.pageKey === pageKeyParam) || {};
    let { url } = curPage;
    this.push(url);
  }

  replacePage(oldPathname, oldSearch, newPathname, newSearch) {
    const url = oldPathname + oldSearch;
    const curPage = this.pages.find(p => p.url === url);
    if (curPage) {
      const newUrl = newPathname + newSearch;
      Object.assign(curPage, { url: newUrl, pathname: newPathname, search: newSearch });
    }
    return curPage;
  }

  closePage(pageKey) {
    const p = this.pages.find(pg => pg.pageKey === pageKey);
    const pi = this.pages.indexOf(p);

    if (pi >= 0) {
      this.pages.splice(pi, 1);

      // redirect to other url
      if (p.url === this.pathname + this.search) {
        const p2 = this.pages[pi - 1];
        if (p2) {
          this.replace(p2.url);
        } else {
          // go home
          this.replace('/');
        }
      }
    }
  }
}

function syncHistoryWithStore(history, store) {
  store.setHistory(history);

  window.history2 = history;

  // link handler to store, to keep this
  const handleLocationChange = store.handleLocationChange.bind(store);

  // subscribe handler to be called when History changes
  history.listen(handleLocationChange);

  const initialLocation = `${history.location.pathname}${history.location.search || ''}`;
  const normalizedInitialLocation = store.setPortalId(initialLocation);

  if (normalizedInitialLocation !== initialLocation) {
    history.replace(normalizedInitialLocation);
  } else {
    // init call
    handleLocationChange(history.location);
  }

  return history;
}

export function initLocation() {
  // make location state
  const loc = new Location();

  // sync with History
  syncHistoryWithStore(createHistory(), loc);

  return loc;
}

export const location = initLocation();
